/**
 * OperatorGate — where an operator supplies the credential, and where the
 * console says out loud that the credential is wrong.
 *
 * Founder ruling 6 (2026-09-08) retires this shape: "Human platform operations
 * must use authenticated operator identity, not an attribution header plus a
 * broadly shared secret." Lane TOKENSPLIT is splitting the shared token now.
 * This screen is built on the door that exists TODAY and it prints the ruling
 * on itself rather than presenting the arrangement as normal — a console that
 * asked for a shared secret with no comment would be teaching operators that
 * this is how platform work is done.
 */
import { useState } from 'react';
import { actorProblems } from '../platform/credentials.js';
import {
  signIn,
  verifyCode,
  GOOGLE_ERROR_MESSAGES,
  GOOGLE_START_PATH,
  readGoogleReturn,
} from '../platform/staffSession.js';
import type { SignInStep } from '../platform/staffSession.js';
import type { PresentedPrincipal } from '../platform/authority.js';

/**
 * `onPresented` HANDS OVER WHAT WAS TYPED, NOT AN ADMITTED ACTOR.
 *
 * This screen used to build the `PlatformActor` itself and pass it up, which
 * made it the thing that decided who gets in. It is not: `consoleAdmission`
 * is, in `platform/authority.ts`, and it default-denies. A gate that mints the
 * credential object it is guarding cannot be the subject of an admission test,
 * because there is no shape it can produce that would be refused.
 */
export function OperatorGate({
  onPresented,
  apiBase,
  refusal,
}: {
  onPresented: (presented: PresentedPrincipal) => void;
  apiBase: string;
  /** The admission refusal to render, when a previous attempt was refused. */
  refusal?: string | null;
}) {
  const [operatorId, setOperatorId] = useState('');
  const [systemToken, setSystemToken] = useState('');
  const [touched, setTouched] = useState(false);
  const problems = actorProblems(operatorId, systemToken);

  return (
    <>
      <StaffSignIn apiBase={apiBase} onPresented={onPresented} />

      <div className="banner">
        <strong>The operator credential below is known to be the wrong shape.</strong>
        <p>
          The platform door authenticates with the shared <code>SYSTEM_TRIGGER_TOKEN</code> and
          attributes with an <code>x-platform-operator</code> header. The header is attribution, not
          authentication: nothing here proves you are who you type. The founder ruled on 2026-09-08
          that human platform operations must use an authenticated operator identity instead, and
          that the shared token must be split so that a scheduler credential cannot publish
          authoritative requirements.
        </p>
        <p>
          Until that lands, this console is the same authority as <code>curl</code> with a nicer
          surface. It does not widen anything. What it does add is that every decision is made with
          the source, the provenance, the effective window and the version history in front of the
          person making it.
        </p>
      </div>

      {apiBase ? null : (
        <div className="banner" role="alert">
          <strong>VITE_PLATFORM_API_URL is not set.</strong> The console reaches the platform API by
          absolute URL on purpose — it must never be same-origin with the customer application — so
          there is no fallback to guess at. Nothing will load until it is set.
        </div>
      )}

      <h2>Operator credential (until individual sign-in is switched on)</h2>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setTouched(true);
          onPresented({ kind: 'platform_operator_credential', operatorId, systemToken });
        }}
      >
        <label htmlFor="operatorId">Operator identity (recorded on every decision)</label>
        <input
          id="operatorId"
          name="operatorId"
          autoComplete="off"
          value={operatorId}
          onChange={(e) => setOperatorId(e.target.value)}
          placeholder="you@phibian.example"
        />

        <label htmlFor="systemToken">Platform credential (held in memory only, never stored)</label>
        <input
          id="systemToken"
          name="systemToken"
          type="password"
          autoComplete="off"
          value={systemToken}
          onChange={(e) => setSystemToken(e.target.value)}
        />

        {touched && problems.length > 0 ? (
          <ul role="alert">
            {problems.map((p) => (
              <li key={p.field}>{p.message}</li>
            ))}
          </ul>
        ) : null}

        {refusal ? (
          <div className="banner" role="alert">
            <strong>Refused.</strong> {refusal}
          </div>
        ) : null}

        <p>
          <button type="submit">Open the console</button>
        </p>
        <p className="quiet">
          Nothing typed here is written to localStorage, sessionStorage, a cookie or IndexedDB. It
          lives in one variable and dies with this tab.
        </p>
      </form>
    </>
  );
}

/**
 * SIGN IN AS YOURSELF (D6): Phibian account password, then the code from an
 * authenticator app. The resulting session token is held exactly where the
 * operator secret is held — one variable, this tab — and is presented with the
 * operator's own email, which the server cross-checks against the session.
 */
/** Shown only where Google sign-in for staff is configured (VITE_STAFF_GOOGLE_SIGN_IN=on). */
const GOOGLE_SIGN_IN = import.meta.env.VITE_STAFF_GOOGLE_SIGN_IN === 'on';

function StaffSignIn({
  apiBase,
  onPresented,
}: {
  apiBase: string;
  onPresented: (presented: PresentedPrincipal) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  // Back from Google: the fragment holds a sealed challenge (or a refusal),
  // never a session. Read once, then cleared from the address bar.
  const [step, setStep] = useState<SignInStep | null>(() => {
    const back = readGoogleReturn(window.location.hash);
    if (!back || back.kind === 'accept') return null;
    window.history.replaceState(null, '', window.location.pathname);
    return back.kind === 'challenge'
      ? { kind: 'challenge', challenge: back.challenge }
      : {
          kind: 'refused',
          message: GOOGLE_ERROR_MESSAGES[back.reason] ?? GOOGLE_ERROR_MESSAGES.sign_in_failed!,
        };
  });
  const [busy, setBusy] = useState(false);
  const challenge = step?.kind === 'challenge' ? step.challenge : null;

  const finish = (next: SignInStep) => {
    setBusy(false);
    if (next.kind === 'signed_in') {
      setPassword('');
      setCode('');
      onPresented({
        kind: 'platform_operator_credential',
        operatorId: next.email,
        systemToken: next.token,
      });
      return;
    }
    setStep(next.kind === 'refused' && challenge ? { ...next } : next);
  };

  return (
    <section>
      <h2>Sign in as yourself</h2>
      <p className="quiet">
        Your Phibian account and an authenticator app. Two-step verification is required; a text
        message is not accepted.
      </p>
      {step?.kind === 'not_enabled' ? (
        <p className="quiet">
          Individual sign-in is not switched on for this environment yet. Use your operator
          credential below.
        </p>
      ) : challenge ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setBusy(true);
            void verifyCode(apiBase, challenge, code.trim()).then(finish);
          }}
        >
          <label htmlFor="staffCode">Code from your authenticator app</label>
          <input
            id="staffCode"
            name="staffCode"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <p>
            <button type="submit" disabled={busy}>
              Verify
            </button>{' '}
            <button type="button" onClick={() => setStep(null)}>
              start again
            </button>
          </p>
        </form>
      ) : (
        <>
          {GOOGLE_SIGN_IN ? (
            <>
              <p>
                <a className="button" href={GOOGLE_START_PATH}>
                  Continue with Google
                </a>{' '}
                <span className="quiet">then the code from your authenticator app.</span>
              </p>
              <h3>Or use an email and password</h3>
            </>
          ) : null}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setBusy(true);
              void signIn(apiBase, email.trim(), password).then(finish);
            }}
          >
            <label htmlFor="staffEmail">Email</label>
            <input
              id="staffEmail"
              name="staffEmail"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label htmlFor="staffPassword">Password</label>
            <input
              id="staffPassword"
              name="staffPassword"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p>
              <button type="submit" disabled={busy || !email || !password}>
                Continue
              </button>
            </p>
          </form>
        </>
      )}
      {step &&
      (step.kind === 'refused' ||
        step.kind === 'mfa_enrollment_required' ||
        step.kind === 'transport') ? (
        <div className="banner" role="alert">
          <strong>Not signed in.</strong> {step.message}
        </div>
      ) : null}
    </section>
  );
}
